import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Modal para desmarcar (resolver) una revisión.
// La nota es obligatoria — se envía al solicitante original.
const ResolveReviewModal = ({ open, onClose, asset, onSuccess }) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const canSubmit = note.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/assets/${asset.id}/toggle-review`,
        { marked: false, note: note.trim() },
        { withCredentials: true },
      );
      toast.success('Revisión resuelta y solicitante notificado');
      onSuccess?.(r.data?.asset);
      setNote('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resolver la revisión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setNote('');
    onClose();
  };

  const requesterName = asset?.marked_for_review_by_name || 'el solicitante';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={handleClose}
      data-testid="resolve-review-modal-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-lg w-full flex flex-col"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="resolve-review-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-500 text-white">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                Resolver revisión
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                Describe cómo se ha resuelto — se enviará a {requesterName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-slate-700/20 rounded"
            aria-label="Cerrar"
            data-testid="resolve-review-close"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="p-4">
          <label
            className="text-xs uppercase tracking-wide font-semibold mb-2 flex items-center gap-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Nota de resolución <span className="text-red-500">*</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Describe cómo se ha resuelto la revisión…"
            className="w-full text-sm rounded-md border p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            style={{
              background: 'var(--color-bg)',
              color: 'var(--color-text-primary)',
              borderColor: 'var(--color-border)',
            }}
            autoFocus
            data-testid="resolve-review-note"
          />
          {note.trim().length === 0 && (
            <p className="text-[11px] text-red-500 mt-1">La nota es obligatoria.</p>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{
              color: 'var(--color-text-primary)',
              borderColor: 'var(--color-border)',
              background: 'transparent',
            }}
            data-testid="resolve-review-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-4 py-2 text-sm font-semibold rounded flex items-center gap-2 transition-colors ${
              canSubmit
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="resolve-review-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar resolución
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveReviewModal;
